import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Search as SearchIcon,
  MoreVertical as MoreVerticalIcon,
  Edit as EditIcon,
  UserPlus as UserPlusIcon,
  Download as DownloadIcon,
  Trash2 as Trash2Icon,
  ArrowUpDown,
  ChevronDown,
} from 'lucide-react';
import { motion } from 'framer-motion';

// shadcn/ui components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';

// SalonHub modules
import { useStore } from '@/store/useStore';
import NewCustomerModal from '@/components/customers/NewCustomerModal';
import MedicalInfoEditor from '@/components/customers/MedicalInfoEditor';
import CustomerListModal, { CustomerListItem } from '@/components/customers/CustomerListModal';
import { useCustomersRealtime } from '@/hooks/useSupabaseRealtime';
import { usePageTitle } from '@/hooks/usePageTitle';
import customersService, { Customer } from '@/services/customersService';
import logger from '../utils/logger';

// Utilities
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);

const getRankColor = (rank: string): string => {
  switch (rank) {
    case 'platinum':
      return 'bg-gradient-to-r from-gray-200 to-gray-300';
    case 'gold':
      return 'bg-gradient-to-r from-yellow-400 to-yellow-500';
    case 'silver':
      return 'bg-gradient-to-r from-gray-300 to-gray-400';
    case 'bronze':
      return 'bg-gradient-to-r from-amber-600 to-amber-700';
    default:
      return 'bg-gray-400';
  }
};

const getStatusBadgeVariant = (status: string): 'default' | 'secondary' => {
  return status === 'active' ? 'default' : 'secondary';
};

const Customers: React.FC = () => {
  usePageTitle('顧客管理');
  const { selectedCustomerId, setSelectedCustomerId, addCustomer } = useStore();
  const navigate = useNavigate();

  // Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');

  // Filter states
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterStaff, setFilterStaff] = useState<string>('all');

  // Customer data
  const [customers, setCustomers] = useState<Customer[]>([]);

  // Modal states
  const [newCustomerModalOpen, setNewCustomerModalOpen] = useState(false);
  const [medicalInfoModalOpen, setMedicalInfoModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);


  // Customer list modal state
  const [customerListModal, setCustomerListModal] = useState({
    open: false,
    customers: [] as CustomerListItem[],
    title: '',
    subtitle: '',
    category: 'risk' as 'risk' | 'upsell' | 'birthday',
  });

  // Error state
  const [currentError, setCurrentError] = useState<any | null>(null);
  const [showError, setShowError] = useState(false);

  // Mobile breakpoint
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load customers on mount
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const response = await customersService.getCustomers();
        setCustomers(response.data);
      } catch (error) {
        logger.error('Failed to load customers:', error);
        handleError(error);
      }
    };
    loadCustomers();
  }, []);

  // Realtime updates
  const reloadCustomers = async () => {
    try {
      logger.log('🔄 Reloading customers due to realtime update...');
      const response = await customersService.getCustomers();
      setCustomers(response.data);
      logger.log(`✅ Customers reloaded: ${response.data.length} total`);
    } catch (error) {
      logger.error('❌ Failed to reload customers:', error);
    }
  };

  useCustomersRealtime(reloadCustomers);

  // Handle navigation from dashboard with selectedCustomerId
  useEffect(() => {
    if (selectedCustomerId && customers.length > 0) {
      const customer = customers.find((c) => String(c.id) === String(selectedCustomerId));
      if (customer) {
        navigate(`/customers/${customer.id}`);
        setSelectedCustomerId(null);
      }
    }
  }, [selectedCustomerId, customers, setSelectedCustomerId, navigate]);

  // Error handling
  const handleError = (error: Error | unknown) => {
    setCurrentError(error);
    setShowError(true);
  };

  const dismissError = () => {
    setShowError(false);
    setCurrentError(null);
  };

  // Filter customers based on status and staff
  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      const matchesStatus = filterStatus === 'all' || customer.status === filterStatus;
      const matchesStaff =
        filterStaff === 'all' || customer.preferredStaff === filterStaff;
      return matchesStatus && matchesStaff;
    });
  }, [customers, filterStatus, filterStaff]);

  // Define table columns
  const columns: ColumnDef<Customer>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="hover:bg-transparent"
          >
            顧客名
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const customer = row.original;
        return (
          <div className="flex items-center gap-3">
            <Avatar className={`h-9 w-9 ${getRankColor(customer.rank || 'bronze')}`}>
              <AvatarFallback className="text-white font-semibold">
                {customer.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">{customer.name}</div>
              <div className="text-sm text-muted-foreground">{customer.nameKana}</div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'customerCode',
      header: '顧客コード',
      cell: ({ row }) => (
        <div className="font-mono text-sm">{row.getValue('customerCode') || 'N/A'}</div>
      ),
    },
    {
      accessorKey: 'email',
      header: '連絡先',
      cell: ({ row }) => {
        const customer = row.original;
        return (
          <div>
            <div className="text-sm">{customer.email}</div>
            <div className="text-sm text-muted-foreground">{customer.phone}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'lastVisit',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="hover:bg-transparent"
          >
            最終来店
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const date = row.getValue('lastVisit') as string;
        return date ? new Date(date).toLocaleDateString('ja-JP') : '未来店';
      },
    },
    {
      accessorKey: 'totalVisits',
      header: ({ column }) => {
        return (
          <div className="text-right">
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="hover:bg-transparent"
            >
              来店回数
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        return <div className="text-right">{row.getValue('totalVisits')}回</div>;
      },
    },
    {
      accessorKey: 'totalSpent',
      header: ({ column }) => {
        return (
          <div className="text-right">
            <Button
              variant="ghost"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="hover:bg-transparent"
            >
              累計金額
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const amount = parseFloat(String(row.getValue('totalSpent')));
        return <div className="text-right font-semibold">{formatCurrency(amount)}</div>;
      },
    },
    {
      accessorKey: 'status',
      header: 'ステータス',
      cell: ({ row }) => {
        const status = row.getValue('status') as string;
        return (
          <Badge variant={getStatusBadgeVariant(status)}>
            {status === 'active' ? 'アクティブ' : '休眠'}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const customer = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">メニューを開く</span>
                <MoreVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>操作</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => navigate(`/customers/${customer.id}`)}>
                <EditIcon className="mr-2 h-4 w-4" />
                編集
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleDeleteCustomer(customer)}
                className="text-destructive"
              >
                <Trash2Icon className="mr-2 h-4 w-4" />
                削除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  // Initialize table
  const table = useReactTable({
    data: filteredCustomers,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  // Handle new customer creation
  const handleNewCustomer = async (customerData: any) => {
    try {
      const newCustomer = await addCustomer(customerData);
      setCustomers((prev) => [...prev, newCustomer as unknown as Customer]);
      setNewCustomerModalOpen(false);

      alert(
        `新規顧客「${newCustomer.name}」様を登録しました！\n\n✅ 顧客ID: ${newCustomer.id}\n✅ 顧客コード: ${newCustomer.customerCode}\n✅ 電話番号: ${newCustomer.phone}\n✅ ランク: ${newCustomer.rank?.toUpperCase()}`
      );
    } catch (error) {
      handleError(error);
    }
  };

  // Handle customer deletion
  const handleDeleteCustomer = async (customer: Customer) => {
    const isConfirmed = window.confirm(
      `【注意】顧客データの削除\n\n${customer.name}様のデータを完全に削除しますか？\n\n⚠️ この操作は取り消せません\n⚠️ 施術履歴も全て削除されます\n\n本当に削除しますか？`
    );

    if (isConfirmed) {
      try {
        await customersService.deleteCustomer(customer.id);
        setCustomers((prev) => prev.filter((c) => c.id !== customer.id));
        alert(`✅ ${customer.name}様のデータを削除しました`);
      } catch (error) {
        handleError(error);
      }
    }
  };

  // Handle medical info save
  const handleMedicalInfoSave = async (medicalInfo: any) => {
    if (!selectedCustomer) return;

    try {
      const updatedCustomer = await customersService.updateCustomer(selectedCustomer.id, {
        medicalInfo: medicalInfo,
      });

      setCustomers((prev) =>
        prev.map((customer) => (customer.id === selectedCustomer.id ? updatedCustomer : customer))
      );

      setSelectedCustomer(updatedCustomer);
      setMedicalInfoModalOpen(false);
      alert(`✅ ${selectedCustomer.name}様の医療情報を更新しました\n\n⚠️ 施術時に自動警告が表示されます`);
    } catch (error) {
      handleError(error);
    }
  };

  // Mobile Card View Component
  const CustomerCard = ({ customer }: { customer: Customer }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      onClick={() => navigate(`/customers/${customer.id}`)}
    >
      <Card className="mb-3 cursor-pointer hover:shadow-lg transition-shadow">
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-3">
              <Avatar className={`h-10 w-10 ${getRankColor(customer.rank || 'bronze')}`}>
                <AvatarFallback className="text-white font-semibold">
                  {customer.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="font-semibold">{customer.name}</div>
                <div className="text-sm text-muted-foreground">{customer.nameKana}</div>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVerticalIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/customers/${customer.id}`)}>
                  <EditIcon className="mr-2 h-4 w-4" />
                  編集
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => handleDeleteCustomer(customer)}
                  className="text-destructive"
                >
                  <Trash2Icon className="mr-2 h-4 w-4" />
                  削除
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            <Badge className={getRankColor(customer.rank || 'bronze')}>
              {customer.rank?.toUpperCase()}
            </Badge>
            <Badge variant={getStatusBadgeVariant(customer.status || 'inactive')}>
              {customer.status === 'active' ? 'アクティブ' : '休眠'}
            </Badge>
            {customer.tags?.map((tag) => (
              <Badge key={tag} variant="outline">
                {tag}
              </Badge>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-muted-foreground">最終来店</div>
              <div className="font-medium">
                {customer.lastVisit
                  ? new Date(customer.lastVisit).toLocaleDateString('ja-JP')
                  : '未来店'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">累計金額</div>
              <div className="font-semibold">{formatCurrency(customer.totalSpent)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">来店回数</div>
              <div className="font-medium">{customer.totalVisits}回</div>
            </div>
            <div>
              <div className="text-muted-foreground">担当スタッフ</div>
              <div className="font-medium">{customer.preferredStaff || '未設定'}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">顧客管理</h1>
      </div>

      {/* Actions Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <Label htmlFor="search" className="sr-only">
                検索
              </Label>
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  id="search"
                  placeholder="名前、電話番号、メールアドレスで検索"
                  value={globalFilter ?? ''}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="status-filter" className="sr-only">
                ステータス
              </Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger id="status-filter">
                  <SelectValue placeholder="ステータス" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべて</SelectItem>
                  <SelectItem value="active">アクティブ</SelectItem>
                  <SelectItem value="inactive">休眠</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="staff-filter" className="sr-only">
                担当スタッフ
              </Label>
              <Select value={filterStaff} onValueChange={setFilterStaff}>
                <SelectTrigger id="staff-filter">
                  <SelectValue placeholder="担当スタッフ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">すべて</SelectItem>
                  <SelectItem value="佐藤">佐藤</SelectItem>
                  <SelectItem value="山田">山田</SelectItem>
                  <SelectItem value="田中">田中</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-4 flex gap-2 justify-end">
              <Button variant="outline" size="default">
                <DownloadIcon className="mr-2 h-4 w-4" />
                エクスポート
              </Button>
              <Button onClick={() => setNewCustomerModalOpen(true)} size="default">
                <UserPlusIcon className="mr-2 h-4 w-4" />
                新規顧客
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Message */}
      {showError && currentError && (
        <Alert variant="destructive">
          <AlertDescription className="flex justify-between items-center">
            <span>{currentError?.message || 'エラーが発生しました'}</span>
            <Button size="sm" variant="ghost" onClick={dismissError}>
              閉じる
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Desktop Table View */}
      {!isMobile && (
        <Card>
          <CardContent className="p-0">
            {/* Column Visibility */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="text-sm text-muted-foreground">
                {table.getFilteredSelectedRowModel().rows.length} / {table.getFilteredRowModel().rows.length} 件選択中
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    列表示 <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => {
                      return (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          className="capitalize"
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) => column.toggleVisibility(!!value)}
                        >
                          {column.id}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Table */}
            <div className="overflow-hidden">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/customers/${row.original.id}`)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-24 text-center">
                        顧客が見つかりません
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-end space-x-2 p-4 border-t">
              <div className="flex-1 text-sm text-muted-foreground">
                {table.getFilteredRowModel().rows.length} 件中{' '}
                {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} -{' '}
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                  table.getFilteredRowModel().rows.length
                )}{' '}
                件を表示
              </div>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  前へ
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  次へ
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mobile Card View */}
      {isMobile && (
        <>
          {filteredCustomers.map((customer) => (
            <CustomerCard key={customer.id} customer={customer} />
          ))}

          {/* Mobile Pagination */}
          <div className="flex items-center justify-between mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              前へ
            </Button>
            <div className="text-sm text-muted-foreground">
              {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              次へ
            </Button>
          </div>
        </>
      )}

      {/* Modals */}
      <NewCustomerModal
        open={newCustomerModalOpen}
        onClose={() => setNewCustomerModalOpen(false)}
        onSave={handleNewCustomer}
      />

      <MedicalInfoEditor
        open={medicalInfoModalOpen}
        onClose={() => setMedicalInfoModalOpen(false)}
        onSave={handleMedicalInfoSave}
        initialData={selectedCustomer?.medicalInfo as any}
        customerName={selectedCustomer?.name || ''}
        customerId={selectedCustomer?.id || ''}
      />

      <CustomerListModal
        open={customerListModal.open}
        onClose={() => setCustomerListModal((prev) => ({ ...prev, open: false }))}
        customers={customerListModal.customers}
        title={customerListModal.title}
        subtitle={customerListModal.subtitle}
        category={customerListModal.category}
      />
    </div>
  );
};

export default Customers;
